import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DatePicker } from "@/components/DatePicker";
import { Stack, Plus, Trash, PencilSimple, Sparkle, ClockClockwise, ListPlus } from "@phosphor-icons/react";
import { format } from "date-fns";
import { toast } from "sonner";

const CALC = [
  { key: "hourly", label: "Hourly rate", hint: "Amount = rate × hours", rateLabel: "Hourly rate ($/hr)" },
  { key: "flat", label: "Flat amount", hint: "Fixed amount per entry", rateLabel: "Flat amount ($)" },
  { key: "percentage", label: "Percentage", hint: "% of (hours × base hourly rate)", rateLabel: "Percentage (%)" },
  { key: "multiplier", label: "Multiplier", hint: "× base hourly rate × hours (e.g. 1.5× OT)", rateLabel: "Multiplier (×)" },
];
const CALC_LABEL = Object.fromEntries(CALC.map((c) => [c.key, c.label]));
const rateLabelFor = (m) => CALC.find((c) => c.key === m)?.rateLabel || "Rate";
const today = () => format(new Date(), "yyyy-MM-dd");

const emptyType = {
  name: "", calc_method: "hourly", rate: "", taxable: true, pensionable: true,
  timing_mode: "current", timing_cycles: "1", effective_date: "", end_date: "", color: "#3b82f6",
};

export default function PayTypes() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const currency = user?.currency || "CAD";

  const [typeOpen, setTypeOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [tf, setTf] = useState(emptyType);
  const [occ, setOcc] = useState("police");
  const [entryFor, setEntryFor] = useState(null); // pay type object
  const [ef, setEf] = useState({ entry_date: today(), hours: "", amount: "", status: "actual" });

  const { data: occupations = [] } = useQuery({ queryKey: ["occupations"], queryFn: async () => (await api.get("/occupations")).data });
  const { data: payTypes = [] } = useQuery({ queryKey: ["pay-types"], queryFn: async () => (await api.get("/pay-types")).data });
  const { data: entries = [] } = useQuery({ queryKey: ["pay-entries"], queryFn: async () => (await api.get("/pay-entries")).data });
  const { data: dash } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });

  const invalidate = () => {
    ["pay-types", "pay-entries"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    qc.invalidateQueries({ queryKey: ["projections"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const buildType = (f) => ({
    name: f.name, calc_method: f.calc_method, rate: parseFloat(f.rate) || 0,
    taxable: f.taxable, pensionable: f.pensionable, color: f.color,
    timing: { mode: f.timing_mode, cycles: parseInt(f.timing_cycles) || 1 },
    effective_date: f.effective_date || null, end_date: f.end_date || null,
  });

  const saveType = useMutation({
    mutationFn: async (f) => editingType
      ? (await api.put(`/pay-types/${editingType}`, buildType(f))).data
      : (await api.post("/pay-types", buildType(f))).data,
    onSuccess: () => { invalidate(); toast.success(editingType ? "Pay type updated." : "Pay type created."); setTypeOpen(false); setEditingType(null); setTf(emptyType); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const deleteType = useMutation({
    mutationFn: async (id) => api.delete(`/pay-types/${id}`),
    onSuccess: () => { invalidate(); toast.success("Pay type removed."); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const applyTemplate = useMutation({
    mutationFn: async () => (await api.post("/pay-types/apply-template", { occupation: occ })).data,
    onSuccess: (d) => { invalidate(); toast.success(d.created ? `Added ${d.created} suggested pay types.` : "You already have these pay types."); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const saveEntry = useMutation({
    mutationFn: async () => {
      const body = { pay_type_id: entryFor.id, entry_date: ef.entry_date, status: ef.status };
      if (entryFor.calc_method === "flat") body.amount = parseFloat(ef.amount) || entryFor.rate;
      else body.hours = parseFloat(ef.hours) || 0;
      return (await api.post("/pay-entries", body)).data;
    },
    onSuccess: () => { invalidate(); toast.success("Entry logged."); setEntryFor(null); setEf({ entry_date: today(), hours: "", amount: "", status: "actual" }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const deleteEntry = useMutation({
    mutationFn: async (id) => api.delete(`/pay-entries/${id}`),
    onSuccess: () => { invalidate(); toast.success("Entry removed."); },
  });

  const openNewType = () => { setEditingType(null); setTf(emptyType); setTypeOpen(true); };
  const openEditType = (p) => {
    setEditingType(p.id);
    setTf({
      name: p.name, calc_method: p.calc_method, rate: String(p.rate ?? ""),
      taxable: p.taxable, pensionable: p.pensionable,
      timing_mode: p.timing?.mode || "current", timing_cycles: String(p.timing?.cycles || 1),
      effective_date: p.effective_date || "", end_date: p.end_date || "", color: p.color || "#3b82f6",
    });
    setTypeOpen(true);
  };
  const openEntry = (p) => { setEntryFor(p); setEf({ entry_date: today(), hours: "", amount: p.calc_method === "flat" ? String(p.rate || "") : "", status: "actual" }); };

  const rateDisplay = (p) => p.calc_method === "flat" ? money(p.rate, currency)
    : p.calc_method === "percentage" ? `${p.rate}%`
    : p.calc_method === "multiplier" ? `${p.rate}×` : `${money(p.rate, currency)}/hr`;

  return (
    <div className="animate-fade-up" data-testid="pay-types-page">
      <div className="mb-8">
        <div className="flex items-center gap-2"><Stack className="h-5 w-5 text-sky-400" weight="bold" /><p className="overline text-sky-400">Universal pay engine</p></div>
        <h1 className="text-3xl font-black tracking-tight mt-1">Pay Types</h1>
        <p className="text-muted-foreground text-sm mt-1">Define unlimited custom pay types for your job. Log entries against them and they flow into your Dashboard and Projections.</p>
      </div>

      {/* This period so far */}
      {dash && (dash.custom_pay > 0 || Object.keys(dash.custom_by_type || {}).length > 0) && (
        <Card className="p-5 mb-6 bg-gradient-to-br from-sky-500/10 to-transparent border-sky-500/30" data-testid="paytypes-period-summary">
          <p className="text-xs uppercase tracking-wide text-sky-300">Custom pay types · this pay period so far</p>
          <p className="text-3xl font-black mt-1" data-testid="paytypes-period-total">{money(dash.custom_pay || 0, currency)}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {Object.entries(dash.custom_by_type || {}).map(([name, val]) => (
              <span key={name} className="inline-flex items-center gap-1.5 rounded-full bg-secondary/60 px-3 py-1 text-xs font-medium">
                {name} · <span className="font-bold text-sky-300">{money(val, currency)}</span>
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Occupation quick start */}
      <Card className="p-5 mb-6 bg-card border-border" data-testid="occupation-quickstart">
        <div className="flex items-center gap-2 mb-3"><Sparkle className="h-4 w-4 text-amber-400" weight="fill" /><p className="font-semibold text-sm">Quick start from your occupation</p></div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <Label>Occupation</Label>
            <Select value={occ} onValueChange={setOcc}>
              <SelectTrigger className="mt-1.5 bg-secondary/50" data-testid="occupation-select"><SelectValue /></SelectTrigger>
              <SelectContent>{occupations.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={() => applyTemplate.mutate()} disabled={applyTemplate.isPending} data-testid="apply-template-button" variant="outline" className="rounded-md font-semibold">
            <ListPlus className="h-4 w-4 mr-1.5" weight="bold" /> Add suggested pay types
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Suggested types are fully editable — rename, re-rate or delete any of them afterwards.</p>
      </Card>

      {/* Pay types list */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold">Your pay types</h2>
        <Dialog open={typeOpen} onOpenChange={(o) => { setTypeOpen(o); if (!o) { setEditingType(null); setTf(emptyType); } }}>
          <DialogTrigger asChild>
            <Button onClick={openNewType} size="sm" className="rounded-md font-semibold" data-testid="add-pay-type-button"><Plus className="h-4 w-4 mr-1.5" weight="bold" /> New pay type</Button>
          </DialogTrigger>
          <DialogContent className="bg-popover border-border max-h-[90vh] overflow-y-auto" data-testid="pay-type-dialog">
            <DialogHeader>
              <DialogTitle>{editingType ? "Edit pay type" : "New pay type"}</DialogTitle>
              <DialogDescription className="text-xs">{CALC.find((c) => c.key === tf.calc_method)?.hint}</DialogDescription>
            </DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); saveType.mutate(tf); }} className="space-y-4">
              <div><Label>Name</Label><Input data-testid="pay-type-name" className="mt-1.5 bg-secondary/50" required value={tf.name} onChange={(e) => setTf({ ...tf, name: e.target.value })} placeholder="e.g. Shift Premium" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Calculation</Label>
                  <Select value={tf.calc_method} onValueChange={(v) => setTf({ ...tf, calc_method: v })}>
                    <SelectTrigger className="mt-1.5 bg-secondary/50" data-testid="pay-type-calc"><SelectValue /></SelectTrigger>
                    <SelectContent>{CALC.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>{rateLabelFor(tf.calc_method)}</Label><Input type="number" step="0.01" min="0" data-testid="pay-type-rate" className="mt-1.5 bg-secondary/50" value={tf.rate} onChange={(e) => setTf({ ...tf, rate: e.target.value })} /></div>
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2"><Switch checked={tf.taxable} onCheckedChange={(v) => setTf({ ...tf, taxable: v })} data-testid="pay-type-taxable" /><Label className="mb-0">Taxable</Label></div>
                <div className="flex items-center gap-2"><Switch checked={tf.pensionable} onCheckedChange={(v) => setTf({ ...tf, pensionable: v })} data-testid="pay-type-pensionable" /><Label className="mb-0">Pensionable</Label></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Paid on</Label>
                  <Select value={tf.timing_mode} onValueChange={(v) => setTf({ ...tf, timing_mode: v })}>
                    <SelectTrigger className="mt-1.5 bg-secondary/50" data-testid="pay-type-timing"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="current">Current paycheck</SelectItem><SelectItem value="next">Next paycheck</SelectItem><SelectItem value="cycles">X cycles later</SelectItem></SelectContent>
                  </Select>
                </div>
                {tf.timing_mode === "cycles" && <div><Label>Cycles later</Label><Input type="number" min="1" className="mt-1.5 bg-secondary/50" value={tf.timing_cycles} onChange={(e) => setTf({ ...tf, timing_cycles: e.target.value })} /></div>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Effective from (optional)</Label><div className="mt-1.5"><DatePicker testId="pay-type-effective" value={tf.effective_date} onChange={(d) => setTf({ ...tf, effective_date: d })} /></div></div>
                <div><Label>Ends (optional)</Label><div className="mt-1.5"><DatePicker testId="pay-type-end" value={tf.end_date} onChange={(d) => setTf({ ...tf, end_date: d })} /></div></div>
              </div>
              <DialogFooter><Button type="submit" disabled={saveType.isPending} data-testid="pay-type-submit" className="rounded-md font-semibold">{saveType.isPending ? "Saving…" : editingType ? "Update" : "Create"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-card border-border overflow-hidden mb-8">
        {payTypes.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground" data-testid="pay-types-empty">No pay types yet. Add suggested ones from your occupation above, or create your own.</div>
        ) : payTypes.map((p) => (
          <div key={p.id} data-testid={`pay-type-row-${p.id}`} className="flex items-center gap-3 px-5 py-4 border-b border-border last:border-0 hover:bg-secondary/30 transition-colors duration-200">
            <span className="h-8 w-8 rounded-md shrink-0" style={{ backgroundColor: (p.color || "#3b82f6") + "33", border: `1px solid ${p.color || "#3b82f6"}` }} />
            <div className="min-w-0 flex-1">
              <p className="font-semibold truncate">{p.name}</p>
              <p className="text-xs text-muted-foreground">{CALC_LABEL[p.calc_method]} · {rateDisplay(p)}{p.taxable ? " · taxable" : ""}{p.pensionable ? " · pensionable" : ""}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => openEntry(p)} data-testid={`log-entry-${p.id}`} className="rounded-md"><Plus className="h-3.5 w-3.5 mr-1" weight="bold" />Log</Button>
            <button onClick={() => openEditType(p)} data-testid={`edit-pay-type-${p.id}`} className="text-muted-foreground hover:text-primary transition-colors duration-200"><PencilSimple className="h-4 w-4" weight="bold" /></button>
            <button onClick={() => deleteType.mutate(p.id)} data-testid={`delete-pay-type-${p.id}`} className="text-muted-foreground hover:text-destructive transition-colors duration-200"><Trash className="h-4 w-4" weight="bold" /></button>
          </div>
        ))}
      </Card>

      {/* Log entry dialog */}
      <Dialog open={!!entryFor} onOpenChange={(o) => { if (!o) setEntryFor(null); }}>
        <DialogContent className="bg-popover border-border" data-testid="pay-entry-dialog">
          <DialogHeader><DialogTitle>Log {entryFor?.name}</DialogTitle><DialogDescription className="text-xs">{entryFor && CALC.find((c) => c.key === entryFor.calc_method)?.hint}</DialogDescription></DialogHeader>
          {entryFor && (
            <form onSubmit={(e) => { e.preventDefault(); saveEntry.mutate(); }} className="space-y-4">
              <div><Label>Date</Label><div className="mt-1.5"><DatePicker testId="pay-entry-date" value={ef.entry_date} onChange={(d) => setEf({ ...ef, entry_date: d })} /></div></div>
              {entryFor.calc_method === "flat" ? (
                <div><Label>Amount ({currency})</Label><Input type="number" step="0.01" min="0" data-testid="pay-entry-amount" className="mt-1.5 bg-secondary/50" value={ef.amount} onChange={(e) => setEf({ ...ef, amount: e.target.value })} /></div>
              ) : (
                <div><Label>Hours</Label><Input type="number" step="0.25" min="0" data-testid="pay-entry-hours" className="mt-1.5 bg-secondary/50" value={ef.hours} onChange={(e) => setEf({ ...ef, hours: e.target.value })} /></div>
              )}
              <div>
                <Label>Status</Label>
                <Select value={ef.status} onValueChange={(v) => setEf({ ...ef, status: v })}>
                  <SelectTrigger className="mt-1.5 bg-secondary/50" data-testid="pay-entry-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="actual">Actual (worked)</SelectItem><SelectItem value="scheduled">Scheduled (projected)</SelectItem></SelectContent>
                </Select>
              </div>
              <DialogFooter><Button type="submit" disabled={saveEntry.isPending} data-testid="pay-entry-submit" className="rounded-md font-semibold">{saveEntry.isPending ? "Saving…" : "Log entry"}</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Recent entries */}
      <div className="flex items-center gap-2 mb-3"><ClockClockwise className="h-4 w-4 text-muted-foreground" weight="bold" /><h2 className="text-base font-bold">Recent pay-type entries</h2></div>
      <Card className="bg-card border-border overflow-hidden">
        {entries.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground" data-testid="pay-entries-empty">No entries logged yet.</div>
        ) : entries.slice(0, 30).map((e) => (
          <div key={e.id} data-testid={`pay-entry-row-${e.id}`} className="flex items-center gap-3 px-5 py-3 border-b border-border last:border-0">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{e.pay_type_name}{e.status === "scheduled" ? <span className="ml-2 text-[10px] uppercase tracking-wide text-sky-400">scheduled</span> : ""}</p>
              <p className="text-xs text-muted-foreground">{format(new Date(e.entry_date + "T00:00:00"), "MMM d, yyyy")}{e.hours ? ` · ${e.hours} h` : ""}</p>
            </div>
            <span className="font-bold text-sm" data-testid={`pay-entry-amount-${e.id}`}>{money(e.amount, currency)}</span>
            <button onClick={() => deleteEntry.mutate(e.id)} data-testid={`delete-pay-entry-${e.id}`} className="text-muted-foreground hover:text-destructive transition-colors duration-200"><Trash className="h-4 w-4" weight="bold" /></button>
          </div>
        ))}
      </Card>
    </div>
  );
}
